/* eslint-disable @next/next/no-img-element */
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import propTypes from '@styled-system/prop-types';
import PropTypes from 'prop-types';
import { cloneElement } from 'react';
import { compose, position, space } from 'styled-system';

import AvatarBadge from './components/AvatarBadge';

import APP from 'constants/app';

const defaultSizes = {
  size1: '5rem',
  size2: '4.5rem',
  size3: '3.5rem',
  size4: '2.5rem',
  size5: '2rem',
};

const mobileSizeMap = {
  size1: 'size2',
  size2: '4rem',
  size3: 'size3',
  size4: 'size4',
  size5: 'size5',
};

const baseStyles = ({ theme }) => css`
  display: block;
  position: relative;
  user-select: none;

  figure {
    align-items: center;
    background-color: ${theme.colors.avatar.bg};
    border: ${theme.border.widths.size1} solid ${theme.colors.avatar.border};
    border-radius: ${theme.border.radius.rounded};
    display: flex;
    flex-direction: column;
    height: 100%;
    justify-content: center;
    position: relative;
    width: 100%;
  }

  img {
    border-radius: ${theme.border.radius.rounded};
    display: block;
    height: 100%;
    overflow: hidden;
    max-width: 100%;
    /* TODO: verify possibility to remove this style */
    vertical-align: top;
    width: 100%;
  }
`;

const sizeStyles = ({ theme, size }) => {
  const currMobileSize = defaultSizes[mobileSizeMap[size]]
    ? defaultSizes[mobileSizeMap[size]]
    : mobileSizeMap[size];

  return css`
    height: ${currMobileSize};
    min-height: ${currMobileSize};
    min-width: ${currMobileSize};
    width: ${currMobileSize};

    ${theme.mediaQueries.lg} {
      height: ${defaultSizes[size]};
      min-height: ${defaultSizes[size]};
      min-width: ${defaultSizes[size]};
      width: ${defaultSizes[size]};
    }
  `;
};

const StyledAvatar = styled('div')`
  ${baseStyles}
  ${sizeStyles}
  ${compose(space, position)}
`;

const StyledAvatarImage = styled('img')`
  ${({ grayscale }) => css`
    filter: ${grayscale && 'grayscale(1)'};
  `}
`;

const getComponentForIcon = ({ icon, iconComponent }) => {
  if (!iconComponent) {
    switch (icon) {
      case 'coupon':
        return (
          <img
            src={`${APP.LOCAL_IMAGE_PATH}/features/avatar/coupon.png`}
            alt="Cupom"
          />
        );
      case 'store':
        return (
          <img
            src={`${APP.LOCAL_IMAGE_PATH}/features/avatar/store.png`}
            alt="Loja"
          />
        );
      case 'user':
        return (
          <img
            src={`${APP.LOCAL_IMAGE_PATH}/features/avatar/user.png`}
            alt="Usuário"
          />
        );
      default:
        return (
          <img
            src={`${APP.LOCAL_IMAGE_PATH}/features/avatar/user.png`}
            alt="Usuário"
          />
        );
    }
  }

  return cloneElement(iconComponent, {
    height: '80%',
    width: '80%',
  });
};

function Avatar({
  admin,
  description,
  grayscale,
  icon,
  iconComponent,
  level,
  loading,
  size,
  sizes,
  src,
  srcSet,
  onImageError,
  ...rest
}) {
  const Icon = getComponentForIcon({ icon, iconComponent });

  return (
    <StyledAvatar size={size} {...rest}>
      <figure>
        {src || srcSet ? (
          <StyledAvatarImage
            alt={description}
            grayscale={grayscale}
            loading={loading}
            src={src}
            srcSet={srcSet}
            sizes={sizes}
            onError={onImageError}
          />
        ) : (
          iconComponent || Icon
        )}
      </figure>
      {level !== null && level >= 0 && icon === 'user' && (
        <AvatarBadge size={size} admin={admin}>
          {level}
        </AvatarBadge>
      )}
    </StyledAvatar>
  );
}

Avatar.SIZES = Object.keys(defaultSizes);

Avatar.defaultProps = {
  admin: false,
  description: undefined,
  grayscale: false,
  icon: 'user',
  iconComponent: undefined,
  level: undefined,
  loading: null,
  size: 'size1',
  sizes: null,
  src: undefined,
  srcSet: null,
  onClick: () => {},
  onImageError: () => {},
};

Avatar.propTypes = {
  /**
   * Renders a admin level badge
   */
  admin: PropTypes.bool,
  /**
   * String containing some descriptive information about the image
   */
  description: PropTypes.string,
  /**
   * Renders the image with a grayscale filter
   */
  grayscale: PropTypes.bool,
  /**
   * Renders an icon when there is no src path for a valid image
   *
   * One of `user`, `store`, `coupon` or an `Icon` type
   */
  icon: PropTypes.string,
  /**
   * Custom icon to be used instead of the icons options
   */
  iconComponent: PropTypes.shape(),
  /**
   * Number to be rendered in badge.
   * If no level is passed, badge wont be rendered.
   * Should be a positive number and less than 100
   */
  // eslint-disable-next-line consistent-return
  level: (props, propName, componentName) => {
    const isWithinValidNumberRange = (number) => number < 0 || number > 99;

    if (
      props[propName] &&
      !Number.isInteger(props[propName]) &&
      isWithinValidNumberRange(props[propName])
    ) {
      return new Error(
        `Invalid prop \`${propName}\` supplied to \`${componentName}\`. Prop \`${propName}\` should be a positive number and less than 100.`
      );
    }
  },
  /**
   * Whether to use HTML loading attribute
   */
  loading: PropTypes.oneOf(['eager', 'lazy']),
  /**
   * Renders a determined size
   */
  size: PropTypes.oneOf(['size1', 'size2', 'size3', 'size4', 'size5']),
  /**
   * A valid `sizes` image attribute to be used alongside `srcSet`
   */
  sizes: PropTypes.string,
  /**
   * Path to a valid image
   */
  src: PropTypes.string,
  /**
   * A valid `srcSet` image attribute
   */
  srcSet: PropTypes.string,
  /**
   * Callback function to be executed on click
   */
  onClick: PropTypes.func,
  /**
   * Callback function to be executed when avatar image is broken
   */
  onImageError: PropTypes.func,
  /**
   * Implements `styled-system` [space](https://styled-system.com/api/#space) utility
   */
  ...propTypes.space,
  /**
   * Implements `styled-system` [position](https://styled-system.com/api/#position) utility
   */
  ...propTypes.position,
};

export default Avatar;
